import React, { useState, useEffect, useCallback } from 'react';
import { Grid, FormControl, MenuItem, Select, InputLabel, IconButton, Menu, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { Visibility, MoreVert, Edit, Delete } from '@material-ui/icons';

import { listSimovsatVehicles, updateSimovsatVehicle, deleteSimovsatVehicle, isSimovsatAvailable } from '~/services/api';
import { SimovsatVehicleAPI, SimovsatVehiclesAPIResponse } from '~/types';
import { handleApiErrorResponse } from '~/services/handleErrors';
import { Table } from '~/components/Table';
import InputComponent from '~/components/InputComponent';
import Button from '~/components/Button';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';
import VehicleDetails from '../VehicleDetails';

const isActiveStatus = (status?: string) => status === 'ACTIVE' || status === 'ATIVO';

const normalizeSimovsatVehicle = (vehicle: any): SimovsatVehicleAPI => ({
  ...vehicle,
  id: String(vehicle.id),
  license_plate: vehicle.license_plate || vehicle.plate_number || '',
  model: vehicle.model || vehicle.additional_notes || vehicle.device_model || '',
  color: vehicle.color || '-',
  manufacture: vehicle.manufacture || vehicle.device_model || '-',
  status: vehicle.status || (vehicle.active ? 'ACTIVE' : 'INACTIVE'),
  model_year: vehicle.model_year,
  created_at: vehicle.created_at || '',
  updated_at: vehicle.updated_at || '',
  customer_id: vehicle.customer_id || vehicle.user_id || null,
  vehicle_customer_type_id: vehicle.vehicle_customer_type_id || '',
  association_id: vehicle.association_id || vehicle.organization_id || '',
});

export default function VehicleSearch() {
  const [licensePlate, setLicensePlate] = useState<string>('');
  const [kmMin, setKmMin] = useState<string>('');
  const [kmMax, setKmMax] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);

  const [vehicles, setVehicles] = useState<SimovsatVehicleAPI[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [count, setCount] = useState<number>(0);

  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [selectedVehicle, setSelectedVehicle] = useState<SimovsatVehicleAPI | null>(null);
  const [openVehicleDetails, setOpenVehicleDetails] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [vehicleForAction, setVehicleForAction] = useState<SimovsatVehicleAPI | null>(null);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [editingVehicle, setEditingVehicle] = useState<Partial<SimovsatVehicleAPI>>({});
  const [deleting, setDeleting] = useState<boolean>(false);

  const searchVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (licensePlate.trim()) {
        filters.license_plate = licensePlate.trim();
      }
      if (kmMin.trim()) {
        filters.km_min = kmMin.trim();
      }
      if (kmMax.trim()) {
        filters.km_max = kmMax.trim();
      }
      if (status) {
        filters.status = status;
      }

      const response = await listSimovsatVehicles(page, perPage, filters);
      if (response.status === 200 && response.data) {
        const data: SimovsatVehiclesAPIResponse = response.data;
        const responseVehicles = (data.data || []).map(normalizeSimovsatVehicle);
        const filteredVehicles = status
          ? responseVehicles.filter(vehicle => vehicle.status === status)
          : responseVehicles;
        const pageStart = (page - 1) * perPage;
        const pageVehicles = filteredVehicles.slice(pageStart, pageStart + perPage);

        setVehicles(pageVehicles);
        setTotal(filteredVehicles.length);
        setCount(filteredVehicles.length);
      } else {
        setVehicles([]);
        setTotal(0);
        setCount(0);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) +
        ': não foi possível buscar veículos. Tente novamente.',
      );
      setOpenSnackbar(true);
      setVehicles([]);
      setTotal(0);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [licensePlate, kmMin, kmMax, status, page, perPage]);

  useEffect(() => {
    if (page > 0 && isSimovsatAvailable()) {
      searchVehicles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const handleSearch = () => {
    setPage(1);
    setHasSearched(true);
    searchVehicles();
  };

  const handleViewDetails = (vehicle: SimovsatVehicleAPI) => {
    setSelectedVehicle(vehicle);
    setOpenVehicleDetails(true);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement> | null, vehicle: SimovsatVehicleAPI) => {
    if (event) {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    } else {
      // Se não houver evento, cria um elemento temporário
      const tempEl = document.createElement('div');
      tempEl.style.position = 'fixed';
      tempEl.style.top = '50%';
      tempEl.style.left = '50%';
      document.body.appendChild(tempEl);
      setAnchorEl(tempEl as any);
      setTimeout(() => {
        if (tempEl.parentNode) {
          document.body.removeChild(tempEl);
        }
      }, 100);
    }
    setVehicleForAction(vehicle);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setVehicleForAction(null);
  };

  const handleEdit = () => {
    if (vehicleForAction) {
      setEditingVehicle(vehicleForAction);
      setOpenEditModal(true);
    }
    handleCloseMenu();
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
    handleCloseMenu();
  };

  const confirmDelete = async () => {
    if (!vehicleForAction) return;

    try {
      setDeleting(true);
      const response = await deleteSimovsatVehicle(vehicleForAction.id);
      if (response.status === 200) {
        setSeverity('success');
        setMessageSnack('Veículo removido com sucesso.');
        setOpenSnackbar(true);
        setOpenDeleteDialog(false);
        setVehicleForAction(null);
        // Recarrega a lista
        searchVehicles();
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível remover o veículo. Tente novamente.',
      );
      setOpenSnackbar(true);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingVehicle.id) return;

    try {
      setLoading(true);
      const response = await updateSimovsatVehicle(editingVehicle.id, {
        license_plate: editingVehicle.license_plate,
        model: editingVehicle.model,
        color: editingVehicle.color,
        manufacture: editingVehicle.manufacture,
        status: editingVehicle.status,
        model_year: editingVehicle.model_year,
      });

      if (response.status === 200) {
        setSeverity('success');
        setMessageSnack('Veículo atualizado com sucesso.');
        setOpenSnackbar(true);
        setOpenEditModal(false);
        setEditingVehicle({});
        // Recarrega a lista
        searchVehicles();
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível atualizar o veículo. Tente novamente.',
      );
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: 20 }} alignItems='flex-end'>
        <Grid item xs={12} sm={6} md={3}>
          <div style={{ width: '100%' }}>
            <InputComponent
              label='Placa'
              name='licensePlate'
              type='text'
              value={licensePlate}
              onChange={e => setLicensePlate(e.target.value.toUpperCase())}
              error={null}
              placeholder='Ex: ABC1234'
            />
          </div>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <div style={{ width: '100%' }}>
            <InputComponent
              label='Km mín.'
              name='kmMin'
              type='number'
              value={kmMin}
              onChange={e => setKmMin(e.target.value)}
              error={null}
            />
          </div>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <div style={{ width: '100%' }}>
            <InputComponent
              label='Km máx.'
              name='kmMax'
              type='number'
              value={kmMax}
              onChange={e => setKmMax(e.target.value)}
              error={null}
            />
          </div>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl style={{ width: '100%', marginTop: 16 }}>
            <Select
              value={status}
              onChange={e => setStatus(e.target.value as string)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected === '') {
                  return <span style={{ color: '#999' }}>Status</span>;
                }
                return isActiveStatus(selected as string) ? 'Ativo' : 'Inativo';
              }}
            >
              <MenuItem value=''>Todos</MenuItem>
              <MenuItem value='ACTIVE'>Ativo</MenuItem>
              <MenuItem value='INACTIVE'>Inativo</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button
            label='Buscar'
            loading={loading}
            onClick={handleSearch}
            filled
            style={{ width: '100%', marginTop: 0 }}
          />
        </Grid>
      </Grid>

      <Table
        isLoading={loading}
        data={vehicles}
        columns={{
          license_plate: {
            title: 'Placa',
          },
          model: {
            title: 'Modelo',
          },
          manufacture: {
            title: 'Dispositivo',
          },
          name: {
            title: 'Nome',
            render: rowData => rowData.name || '-',
          },
          status: {
            title: 'Status',
            render: rowData => (isActiveStatus(rowData.status) ? 'Ativo' : 'Inativo'),
          },
          lastPosition: {
            title: 'Última posição',
            render: rowData => rowData.lastPosition || '-',
          },
        }}
        actions={[
          {
            icon: () => <Visibility />,
            tooltip: 'Ver detalhes',
            onClick: (_, rowData) => handleViewDetails(rowData as SimovsatVehicleAPI),
          },
          {
            icon: () => <MoreVert />,
            tooltip: 'Opções',
            onClick: (event, rowData) => {
              if (event && event.currentTarget) {
                handleOpenMenu(event as any, rowData as SimovsatVehicleAPI);
              } else {
                handleOpenMenu(null, rowData as SimovsatVehicleAPI);
              }
            },
          },
        ]}
        localization={{
          body: {
            emptyDataSourceMessage: loading ? 'Carregando...' : 'Nenhum veículo encontrado. Use os filtros para buscar.',
          },
          pagination: {
            labelRowsSelect: 'veículos',
          },
        }}
        options={{
          showTitle: false,
          pageSize: perPage,
          pageSizeOptions: [10, 20, 50, 100],
          paging: true,
          filtering: false,
          search: false,
        }}
        onChangePage={(newPage, newPageSize) => {
          setPage(newPage + 1);
          if (newPageSize !== perPage) {
            setPerPage(newPageSize);
          }
        }}
        onChangeRowsPerPage={newPageSize => {
          setPage(1);
          setPerPage(newPageSize);
        }}
      />

      {selectedVehicle && (
        <VehicleDetails
          vehicle={selectedVehicle}
          open={openVehicleDetails}
          onClose={() => {
            setOpenVehicleDetails(false);
            setSelectedVehicle(null);
          }}
        />
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleEdit}>
          <Edit style={{ marginRight: 8 }} fontSize='small' />
          Editar
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Delete style={{ marginRight: 8 }} fontSize='small' />
          Remover
        </MenuItem>
      </Menu>

      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Editar Veículo</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 10 }} alignItems='flex-end'>
            <Grid item xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <InputComponent
                  label='Placa'
                  name='license_plate'
                  type='text'
                  value={editingVehicle.license_plate || ''}
                  onChange={e =>
                    setEditingVehicle({ ...editingVehicle, license_plate: e.target.value.toUpperCase() })
                  }
                  error={null}
                  placeholder='Ex: ABC1234'
                  style={{ marginTop: 0, marginBottom: 0 }}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <InputComponent
                  label='Modelo'
                  name='model'
                  type='text'
                  value={editingVehicle.model || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, model: e.target.value })}
                  error={null}
                  placeholder='Ex: Civic'
                  style={{ marginTop: 0, marginBottom: 0 }}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <InputComponent
                  label='Cor'
                  name='color'
                  type='text'
                  value={editingVehicle.color || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, color: e.target.value })}
                  error={null}
                  placeholder='Ex: Branco'
                  style={{ marginTop: 0, marginBottom: 0 }}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <InputComponent
                  label='Fabricante'
                  name='manufacture'
                  type='text'
                  value={editingVehicle.manufacture || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, manufacture: e.target.value })}
                  error={null}
                  placeholder='Ex: Honda'
                  style={{ marginTop: 0, marginBottom: 0 }}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl style={{ width: '100%', marginTop: 16, marginBottom: 0 }}>
                <InputLabel id='edit-status-label'>Status</InputLabel>
                <Select
                  labelId='edit-status-label'
                  value={editingVehicle.status || ''}
                  onChange={e =>
                    setEditingVehicle({
                      ...editingVehicle,
                      status: (e.target.value as 'ACTIVE' | 'INACTIVE') || undefined,
                    })
                  }
                >
                  <MenuItem value='ACTIVE'>Ativo</MenuItem>
                  <MenuItem value='INACTIVE'>Inativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <InputComponent
                  label='Ano'
                  name='model_year'
                  type='number'
                  value={editingVehicle.model_year?.toString() || ''}
                  onChange={e =>
                    setEditingVehicle({ ...editingVehicle, model_year: parseInt(e.target.value) || undefined })
                  }
                  error={null}
                  placeholder='Ex: 2020'
                  style={{ marginTop: 0, marginBottom: 0 }}
                />
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px' }}>
          <Button
            label='Cancelar'
            onClick={() => setOpenEditModal(false)}
            backgroundColor='#E0E0E0'
            color='#333'
            style={{ width: '120px', marginRight: 8 }}
          />
          <Button
            label='Salvar'
            filled
            onClick={handleSaveEdit}
            loading={loading}
            style={{ width: '120px' }}
          />
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmar Remoção</DialogTitle>
        <DialogContent>
          <p>
            Tem certeza que deseja remover o veículo <strong>{vehicleForAction?.license_plate}</strong>?
          </p>
          <p style={{ fontSize: 12, color: '#666', marginTop: 10 }}>
            Esta ação não pode ser desfeita.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px' }}>
          <Button
            label='Cancelar'
            onClick={() => setOpenDeleteDialog(false)}
            style={{ width: '120px', marginRight: 8 }}
          />
          <Button
            label='Remover'
            filled
            onClick={confirmDelete}
            loading={deleting}
            style={{ width: '120px' }}
          />
        </DialogActions>
      </Dialog>

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </>
  );
}
